import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { selectAccountData } from '../redux/main/accountSlice';
import { selectUserData } from '../redux/main/userSlice';
import { useColorPalette } from '../hooks/useColorPalette';
import DynamicTitle from '../components/DynamicTitle';
import DataTable from '../components/DataTable';
import LoadingSection from '../components/LoadingSection';
import { getApiBaseUrl } from '../config/api.config';
import {
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Badge,
    Card,
    CardBody,
    CardTitle,
    Row,
    Col,
    ButtonGroup
} from 'reactstrap';
import Select2 from '../components/Select2';
import EmployeeSelect from '../components/EmployeeSelect';
import moment from 'moment';
import DynamicIcon from '../components/DynamicIcon';

export default function AppNotifications() {
    const dispatch = useDispatch();
    const palette = useColorPalette();
    const navigate = useNavigate();
    const accountData = useSelector(selectAccountData);
    const userData = useSelector(selectUserData);
    const token = localStorage.getItem('installamelo-auth-token');

    // Stati per la tabella
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');

    // Stati per il modale di creazione/modifica
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' o 'view'
    const [currentNotification, setCurrentNotification] = useState(null);

    // Stati per il form
    const [formData, setFormData] = useState({
        title: '',
        body: '',
        imageUrl: '',
        targetType: 'all',
        targetUserIds: [],
        targetRoles: [],
        actionType: 'none',
        actionData: {},
        sendImmediately: true,
        scheduledDate: '',
        priority: 'normal'
    });

    // Stati per statistiche
    const [stats, setStats] = useState(null);
    
    // Stati per selezione utenti/gruppi
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [userGroups, setUserGroups] = useState([]);

    const searchTimeout = useRef(null);
    
    // Carica gruppi utenti
    const loadUserGroups = async () => {
        if (!accountData?._id) return;
        try {
            const response = await fetch(
                `${getApiBaseUrl()}/userGroups/list?accountId=${accountData._id}`,
                { headers: { 'auth-token': token } }
            );
            if (response.ok) {
                const data = await response.json();
                setUserGroups(data);
            }
        } catch (error) {
            console.error('Errore caricamento gruppi:', error);
        }
    };
    
    useEffect(() => {
        if (accountData?._id) {
            loadUserGroups();
        }
    }, [accountData?._id]);

    // Carica statistiche
    useEffect(() => {
        if (accountData?._id) {
            loadStats();
        }
    }, [accountData?._id]);

    const loadStats = async () => {
        try {
            const response = await fetch(
                `${getApiBaseUrl()}/pushNotifications/stats?accountId=${accountData._id}`,
                {
                    headers: {
                        'auth-token': token
                    }
                }
            );

            if (response.ok) {
                const data = await response.json();
                setStats(data.data);
            }
        } catch (error) {
            console.error('Error loading stats:', error);
        }
    };

    // Funzione per caricare i dati
    const loadNotifications = async (pageIndex = 0, pageSize = 25) => {
        if (!accountData?._id) return;
        setLoading(true);
        try {
            const start = pageIndex * pageSize;
            const searchValue = searchTerm.length >= 2 ? searchTerm : '';

            const url = `${getApiBaseUrl()}/pushNotifications/list?accountId=${accountData._id}&start=${start}&length=${pageSize}&search[value]=${searchValue}&draw=1`;

            const response = await fetch(url, {
                headers: {
                    'auth-token': token
                }
            });

            if (response.ok) {
                const data = await response.json();
                const parsedData = typeof data === 'string' ? JSON.parse(data) : data;

                setNotifications(parsedData.data || []);
                setTotalRecords(parsedData.recordsFiltered || 0);
            } else {
                toast.error('Errore nel caricamento delle notifiche');
            }
        } catch (error) {
            console.error('Error loading notifications:', error);
            toast.error('Errore nel caricamento delle notifiche');
        } finally {
            setLoading(false);
        }
    };

    // Carica notifiche all'avvio
    useEffect(() => {
        if (accountData?._id) {
            loadNotifications();
        }
    }, [accountData?._id]);

    // Gestione ricerca
    const handleSearch = (value) => {
        setSearchTerm(value);

        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }

        searchTimeout.current = setTimeout(() => {
            loadNotifications(0, 25);
        }, 300);
    };

    // Gestione cambio pagina
    const handlePageChange = (pageIndex, pageSize) => {
        loadNotifications(pageIndex, pageSize);
    };

    // Apri modale per creare nuova notifica
    const handleCreateNew = () => {
        setModalMode('create');
        setCurrentNotification(null);
        setFormData({
            title: '',
            body: '',
            imageUrl: '',
            targetType: 'all',
            targetUserIds: [],
            targetRoles: [],
            actionType: 'none',
            actionData: {},
            sendImmediately: true,
            scheduledDate: '',
            priority: 'normal'
        });
        setSelectedUsers([]);
        setSelectedGroup(null);
        setShowModal(true);
    };

    // Apri modale per visualizzare notifica
    const handleViewNotification = (notification) => {
        setModalMode('view');
        setCurrentNotification(notification);
        setShowModal(true);
    };

    // Chiudi modale
    const handleCloseModal = () => {
        setShowModal(false);
        setCurrentNotification(null);
    };

    // Gestione cambio form
    const handleFormChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Salva e invia notifica
    const handleSaveAndSend = async () => {
        // Validazione
        if (!formData.title.trim()) {
            toast.error('Il titolo è obbligatorio');
            return;
        }
        if (!formData.body.trim()) {
            toast.error('Il messaggio è obbligatorio');
            return;
        }
        
        // Validazione destinatari
        if (formData.targetType === 'specific_users' && selectedUsers.length === 0) {
            toast.error('Seleziona almeno un utente');
            return;
        }
        if (formData.targetType === 'user_group' && !selectedGroup) {
            toast.error('Seleziona un gruppo');
            return;
        }

        try {
            // Prepara targetUserIds in base al tipo di destinatario
            let targetUserIds = [];
            if (formData.targetType === 'specific_users') {
                targetUserIds = selectedUsers.map(u => u.value);
            } else if (formData.targetType === 'user_group' && selectedGroup) {
                // Ottieni gli userIds dal gruppo
                const group = userGroups.find(g => g._id === selectedGroup);
                if (group && group.members) {
                    targetUserIds = group.members.map(m => m.userId);
                }
            }
            
            const pushNotification = {
                ...formData,
                targetUserIds,
                targetGroupId: formData.targetType === 'user_group' ? selectedGroup : null,
                accountId: accountData._id,
                senderUserId: userData._id,
                senderUserLabel: `${userData.name} ${userData.surname}`,
                senderUserAvatar: userData.avatar
            };

            console.log('Creazione notifica con dati:', pushNotification);

            // Crea la notifica
            const createResponse = await fetch(`${getApiBaseUrl()}/pushNotifications`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                body: JSON.stringify({ pushNotification })
            });

            if (!createResponse.ok) {
                const errorText = await createResponse.text();
                console.error('Errore creazione notifica:', errorText);
                
                // Prova a parsare il messaggio di errore dal backend
                let errorMessage = 'Errore nella creazione della notifica';
                try {
                    const errorData = JSON.parse(errorText);
                    errorMessage = errorData.message || errorMessage;
                } catch (e) {
                    errorMessage = errorText || errorMessage;
                }
                
                throw new Error(errorMessage);
            }

            const createResultText = await createResponse.text();
            console.log('Risposta creazione (raw):', createResultText);
            
            const createResult = JSON.parse(createResultText);
            console.log('Risposta creazione (parsed):', createResult);
            
            // Il backend restituisce direttamente l'ID, non un oggetto con proprietà data
            const notificationId = typeof createResult === 'string' ? createResult : createResult.data || createResult._id;
            console.log('Notification ID ottenuto:', notificationId);

            if (!notificationId) {
                throw new Error('ID notifica non ricevuto dal server');
            }

            // Se sendImmediately è true, invia subito
            if (formData.sendImmediately) {
                console.log('Invio notifica con ID:', notificationId);
                
                const sendResponse = await fetch(`${getApiBaseUrl()}/pushNotifications/send`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': token
                    },
                    body: JSON.stringify({ pushNotificationId: notificationId })
                });

                if (!sendResponse.ok) {
                    const errorText = await sendResponse.text();
                    console.error('Errore invio notifica:', errorText);
                    
                    // Prova a parsare il messaggio di errore dal backend
                    let errorMessage = 'Errore nell\'invio della notifica';
                    try {
                        const errorData = JSON.parse(errorText);
                        errorMessage = errorData.message || errorMessage;
                    } catch (e) {
                        errorMessage = errorText || errorMessage;
                    }
                    
                    throw new Error(errorMessage);
                }

                const sendResult = await sendResponse.json();
                console.log('Risultato invio:', sendResult);

                toast.success('Notifica inviata con successo!');
            } else {
                toast.success('Notifica schedulata con successo!');
            }

            handleCloseModal();
            loadNotifications();
            loadStats();
        } catch (error) {
            console.error('Error saving notification:', error);
            toast.error(error.message || 'Errore nel salvataggio della notifica');
        }
    };

    // Colonne della tabella
    const columns = useMemo(() => [
        {
            title: 'Codice',
            id: 'code',
            key: 'code',
            width: '10%',
            render: (item) => (
                <span className="font-weight-bold" style={{ color: palette.primary }}>
                    {item.code || '-'}
                </span>
            )
        },
        {
            title: 'Tipo',
            id: 'actionType',
            key: 'actionType',
            width: '10%',
            render: (item) => {
                const typeConfig = {
                    none: { color: 'secondary', text: 'Generale', icon: 'fa-solid fa-bell' },
                    open_url: { color: 'info', text: 'Link', icon: 'fa-solid fa-link' },
                    open_screen: { color: 'primary', text: 'Schermata', icon: 'fa-solid fa-mobile-screen' },
                    open_appointment: { color: 'warning', text: 'Appuntamento', icon: 'fa-solid fa-calendar' }
                };
                const config = typeConfig[item.actionType] || typeConfig.none;
                return (
                    <Badge color={config.color}>
                        <i className={`${config.icon} me-1`}></i>
                        {config.text}
                    </Badge>
                );
            }
        },
        {
            title: 'Titolo',
            id: 'title',
            key: 'title',
            width: '20%',
            render: (item) => <div className="text-truncate" style={{ maxWidth: '200px' }}>{item.title || '-'}</div>
        },
        {
            title: 'Messaggio',
            id: 'body',
            key: 'body',
            width: '25%',
            render: (item) => (
                <div className="text-truncate text-muted" style={{ maxWidth: '250px' }}>
                    {item.body || '-'}
                </div>
            )
        },
        {
            title: 'Destinatari',
            id: 'targetType',
            key: 'targetType',
            width: '10%',
            render: (item) => {
                const badges = {
                    all: { color: 'primary', text: 'Tutti' },
                    specific_users: { color: 'info', text: `${item.totalRecipients || 0} utenti` },
                    role_based: { color: 'warning', text: 'Per ruolo' }
                };
                const badge = badges[item.targetType] || badges.all;
                return <Badge color={badge.color}>{badge.text}</Badge>;
            }
        },
        {
            title: 'Stato',
            id: 'status',
            key: 'status',
            width: '10%',
            render: (item) => {
                const statusConfig = {
                    draft: { color: 'secondary', text: 'Bozza', icon: 'fi-rr-edit' },
                    scheduled: { color: 'info', text: 'Pianificata', icon: 'fi-rr-clock' },
                    sent: { color: 'success', text: 'Inviata', icon: 'fi-rr-check' },
                    failed: { color: 'danger', text: 'Fallita', icon: 'fi-rr-cross' }
                };
                const config = statusConfig[item.status] || statusConfig.draft;
                return (
                    <Badge color={config.color}>
                        <i className={`fi ${config.icon} me-1`}></i>
                        {config.text}
                    </Badge>
                );
            }
        },
        {
            title: 'Data invio',
            id: 'sentDate',
            key: 'sentDate',
            width: '15%',
            render: (item) => item.sentDate ? moment(item.sentDate).format('DD/MM/YYYY HH:mm') : '-'
        }
    ], [palette]);

    return (
        <div className="container-fluid p-4">
            <DynamicTitle title="Notifiche Push App Mobile" />

            {/* Header con statistiche */}
            {stats && (
                <Row className="mb-4">
                    <Col md={3}>
                        <Card className="border-0 shadow-sm">
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div 
                                        className="rounded-circle p-3 me-3"
                                        style={{ backgroundColor: palette.primary + '20' }}
                                    >
                                        <i className="fi fi-rr-bell" style={{ fontSize: '24px', color: palette.primary }}></i>
                                    </div>
                                    <div>
                                        <h6 className="text-muted mb-1">Totale Notifiche</h6>
                                        <h3 className="mb-0">{stats.totalNotifications || 0}</h3>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm">
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div 
                                        className="rounded-circle p-3 me-3"
                                        style={{ backgroundColor: '#28a745' + '20' }}
                                    >
                                        <i className="fi fi-rr-check-circle" style={{ fontSize: '24px', color: '#28a745' }}></i>
                                    </div>
                                    <div>
                                        <h6 className="text-muted mb-1">Inviate</h6>
                                        <h3 className="mb-0">
                                            {stats.byStatus?.find(s => s._id === 'sent')?.count || 0}
                                        </h3>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm">
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div 
                                        className="rounded-circle p-3 me-3"
                                        style={{ backgroundColor: '#17a2b8' + '20' }}
                                    >
                                        <i className="fi fi-rr-clock" style={{ fontSize: '24px', color: '#17a2b8' }}></i>
                                    </div>
                                    <div>
                                        <h6 className="text-muted mb-1">Pianificate</h6>
                                        <h3 className="mb-0">
                                            {stats.byStatus?.find(s => s._id === 'scheduled')?.count || 0}
                                        </h3>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="border-0 shadow-sm">
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div 
                                        className="rounded-circle p-3 me-3"
                                        style={{ backgroundColor: '#ffc107' + '20' }}
                                    >
                                        <i className="fi fi-rr-users-alt" style={{ fontSize: '24px', color: '#ffc107' }}></i>
                                    </div>
                                    <div>
                                        <h6 className="text-muted mb-1">Destinatari Totali</h6>
                                        <h3 className="mb-0">
                                            {stats.byStatus?.reduce((acc, s) => acc + (s.totalRecipients || 0), 0) || 0}
                                        </h3>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Tabella */}
            <Card className="border-0 shadow-sm">
                <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">
                            <DynamicIcon icon="fi-rr-bell" className="me-2" />
                            Lista Notifiche Push
                        </h5>
                        <Button
                            color="primary"
                            onClick={handleCreateNew}
                            style={{ backgroundColor: palette.primary, borderColor: palette.primary }}
                        >
                            <i className="fi fi-rr-plus me-2"></i>
                            Nuova Notifica
                        </Button>
                    </div>

                    <DataTable
                        columns={columns}
                        data={Array.isArray(notifications) ? notifications : []}
                        totalRecords={totalRecords}
                        onPageChange={handlePageChange}
                        onSearch={handleSearch}
                        loading={loading}
                        onRowClick={handleViewNotification}
                        searchPlaceholder="Cerca notifiche..."
                    />
                </CardBody>
            </Card>

            {/* Modale Creazione/Visualizzazione */}
            <Modal isOpen={showModal} toggle={handleCloseModal} size="lg">
                <ModalHeader toggle={handleCloseModal}>
                    {modalMode === 'create' ? (
                        <>
                            <i className="fi fi-rr-plus me-2"></i>
                            Nuova Notifica Push
                        </>
                    ) : (
                        <>
                            <i className="fi fi-rr-eye me-2"></i>
                            Dettaglio Notifica
                        </>
                    )}
                </ModalHeader>
                <ModalBody>
                    {modalMode === 'create' ? (
                        <Form>
                            <FormGroup>
                                <Label for="title">Titolo *</Label>
                                <Input
                                    type="text"
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => handleFormChange('title', e.target.value)}
                                    placeholder="Titolo della notifica"
                                    maxLength={100}
                                />
                                <small className="text-muted">{formData.title.length}/100 caratteri</small>
                            </FormGroup>

                            <FormGroup>
                                <Label for="body">Messaggio *</Label>
                                <Input
                                    type="textarea"
                                    id="body"
                                    value={formData.body}
                                    onChange={(e) => handleFormChange('body', e.target.value)}
                                    placeholder="Contenuto del messaggio"
                                    rows={4}
                                    maxLength={500}
                                />
                                <small className="text-muted">{formData.body.length}/500 caratteri</small>
                            </FormGroup>

                            <FormGroup>
                                <Label for="targetType">Destinatari</Label>
                                <Input
                                    type="select"
                                    id="targetType"
                                    value={formData.targetType}
                                    onChange={(e) => {
                                        handleFormChange('targetType', e.target.value);
                                        // Reset selezioni quando cambia il tipo
                                        setSelectedUsers([]);
                                        setSelectedGroup(null);
                                    }}
                                >
                                    <option value="all">Tutti i collaboratori</option>
                                    <option value="specific_users">Collaboratore specifico</option>
                                    <option value="user_group">Gruppo collaboratori</option>
                                </Input>
                            </FormGroup>
                            
                            {/* Selezione utente specifico */}
                            {formData.targetType === 'specific_users' && (
                                <FormGroup>
                                    <Label>Seleziona Utente/i</Label>
                                    <EmployeeSelect
                                        selectedOptions={selectedUsers}
                                        setSelectedOptions={setSelectedUsers}
                                        isMulti={true}
                                        placeholder="Cerca e seleziona utenti..."
                                    />
                                    <small className="text-muted">
                                        {selectedUsers.length} utenti selezionati
                                    </small>
                                </FormGroup>
                            )}
                            
                            {/* Selezione gruppo */}
                            {formData.targetType === 'user_group' && (
                                <FormGroup>
                                    <Label>Seleziona Gruppo</Label>
                                    <Input
                                        type="select"
                                        value={selectedGroup || ''}
                                        onChange={(e) => setSelectedGroup(e.target.value || null)}
                                    >
                                        <option value="">-- Seleziona un gruppo --</option>
                                        {userGroups.map(group => (
                                            <option key={group._id} value={group._id}>
                                                {group.name} ({group.members?.length || 0} membri)
                                            </option>
                                        ))}
                                    </Input>
                                    {userGroups.length === 0 && (
                                        <small className="text-warning">
                                            Nessun gruppo disponibile. Creane uno dalla pagina Utenti.
                                        </small>
                                    )}
                                    {selectedGroup && (
                                        <small className="text-muted">
                                            {userGroups.find(g => g._id === selectedGroup)?.members?.length || 0} destinatari
                                        </small>
                                    )}
                                </FormGroup>
                            )}

                            <FormGroup>
                                <Label for="priority">Priorità</Label>
                                <Input
                                    type="select"
                                    id="priority"
                                    value={formData.priority}
                                    onChange={(e) => handleFormChange('priority', e.target.value)}
                                >
                                    <option value="normal">Normale</option>
                                    <option value="high">Alta</option>
                                </Input>
                            </FormGroup>

                            <FormGroup check>
                                <Label check>
                                    <Input
                                        type="checkbox"
                                        checked={formData.sendImmediately}
                                        onChange={(e) => handleFormChange('sendImmediately', e.target.checked)}
                                    />
                                    {' '}Invia immediatamente
                                </Label>
                            </FormGroup>

                            {!formData.sendImmediately && (
                                <FormGroup className="mt-3">
                                    <Label for="scheduledDate">Data e ora programmata</Label>
                                    <Input
                                        type="datetime-local"
                                        id="scheduledDate"
                                        value={formData.scheduledDate}
                                        onChange={(e) => handleFormChange('scheduledDate', e.target.value)}
                                    />
                                </FormGroup>
                            )}
                        </Form>
                    ) : (
                        currentNotification && (
                            <div>
                                <div className="mb-3">
                                    <strong>Codice:</strong> {currentNotification.code}
                                </div>
                                <div className="mb-3">
                                    <strong>Titolo:</strong> {currentNotification.title}
                                </div>
                                <div className="mb-3">
                                    <strong>Messaggio:</strong>
                                    <div className="mt-2 p-3 bg-light rounded">
                                        {currentNotification.body}
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <strong>Stato:</strong>{' '}
                                    <Badge color={currentNotification.status === 'sent' ? 'success' : 'info'}>
                                        {currentNotification.status}
                                    </Badge>
                                </div>
                                <div className="mb-3">
                                    <strong>Data invio:</strong>{' '}
                                    {currentNotification.sentDate
                                        ? moment(currentNotification.sentDate).format('DD/MM/YYYY HH:mm')
                                        : 'Non ancora inviata'}
                                </div>
                                <div className="mb-3">
                                    <strong>Destinatari:</strong> {currentNotification.totalRecipients || 0}
                                </div>
                                <div className="mb-3">
                                    <strong>Invii riusciti:</strong> {currentNotification.successfulSends || 0}
                                </div>
                                {currentNotification.failedSends > 0 && (
                                    <div className="mb-3">
                                        <strong>Invii falliti:</strong>{' '}
                                        <span className="text-danger">{currentNotification.failedSends}</span>
                                    </div>
                                )}
                            </div>
                        )
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={handleCloseModal}>
                        {modalMode === 'create' ? 'Annulla' : 'Chiudi'}
                    </Button>
                    {modalMode === 'create' && (
                        <Button
                            style={{ backgroundColor: palette.primary, borderColor: palette.primary }}
                            onClick={handleSaveAndSend}
                        >
                            <i className="fi fi-rr-paper-plane me-2"></i>
                            {formData.sendImmediately ? 'Invia Ora' : 'Pianifica'}
                        </Button>
                    )}
                </ModalFooter>
            </Modal>
        </div>
    );
}
